import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Upload, Loader2, Trash2, ExternalLink } from "lucide-react";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";

interface PitchDeckUploadProps {
  currentPitchDeckUrl: string | null;
}

export function PitchDeckUpload({ currentPitchDeckUrl }: PitchDeckUploadProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Use signed URL for private pitch-decks bucket
  const { url: resolvedUrl, loading: urlLoading } = useSignedStorageUrl("pitch-decks", currentPitchDeckUrl);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type - PDF only
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file only");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const filePath = `${user.id}/pitch-deck.pdf`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("pitch-decks")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Update profile with storage PATH (not full URL)
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ pitch_deck_url: filePath })
        .eq("id", user.id);

      if (updateError) throw updateError;

      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Pitch deck uploaded successfully!");
    } catch (error: any) {
      toast.error("Failed to upload pitch deck: " + error.message);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    if (!user?.id || !currentPitchDeckUrl) return;

    setDeleting(true);
    try {
      const filePath = `${user.id}/pitch-deck.pdf`;

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from("pitch-decks")
        .remove([filePath]);

      if (deleteError) throw deleteError;

      // Remove URL from profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ pitch_deck_url: null })
        .eq("id", user.id);

      if (updateError) throw updateError;

      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Pitch deck removed");
    } catch (error: any) {
      toast.error("Failed to remove pitch deck: " + error.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Pitch Deck
        </CardTitle>
        <CardDescription>
          Upload your pitch deck in PDF format (max 10MB)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentPitchDeckUrl ? (
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">pitch-deck.pdf</p>
                <p className="text-sm text-muted-foreground">{urlLoading ? "Loading..." : "Uploaded"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={urlLoading || !resolvedUrl}
                onClick={() => resolvedUrl && window.open(resolvedUrl, '_blank')}
              >
                {urlLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" />
                )}
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Replace
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
                className="text-destructive hover:text-destructive"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
          >
            {uploading ? (
              <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" />
            ) : (
              <>
                <Upload className="h-10 w-10 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">Click to upload your pitch deck</p>
                <p className="text-xs text-muted-foreground mt-1">PDF only, max 10MB</p>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleUpload}
          className="hidden"
        />
      </CardContent>
    </Card>
  );
}
